import { memo, useCallback } from 'react';
import { Avatar } from '../../base/avatar/avatar';
import styles from './bannerGallery.module.scss';

type BannerOptionProps = {
  bannerUrl: string;
  index: number;
  isSelected: boolean;
  onSelect: (bannerUrl: string) => void;
};

const BannerOption = memo(function BannerOption({
  bannerUrl,
  index,
  isSelected,
  onSelect
}: BannerOptionProps) {
  const handleClick = useCallback(() => {
    onSelect(bannerUrl);
  }, [onSelect, bannerUrl]);

  return (
    <button
      type="button"
      className={`${styles.bannerOption} ${isSelected ? styles.bannerOptionSelected : ''}`}
      onClick={handleClick}
      aria-label={`Select banner ${index + 1}`}
    >
      <Avatar
        src={bannerUrl}
        className={styles.bannerOptionImage}
        alt={`Banner option ${index + 1}`}
      />
    </button>
  );
});

export type BannerGalleryProps = {
  bannerOptions: string[];
  selectedBanner: string | undefined;
  onSelect: (bannerUrl: string) => void;
  isRequired?: boolean;
  label?: string;
  placeholder?: string;
};

function BannerGallery({
  bannerOptions,
  selectedBanner,
  onSelect,
  isRequired,
  label,
  placeholder
}: BannerGalleryProps) {
  return (
    <div className={styles.bannerGallery}>
      {label && (
        <div className={styles.label}>
          {label}
          {isRequired && <span className={styles.labelRequired}>*</span>}
        </div>
      )}
      <div className={styles.galleryOptions}>
        {bannerOptions.map((bannerUrl, index) => (
          <BannerOption
            key={bannerUrl}
            bannerUrl={bannerUrl}
            index={index}
            isSelected={selectedBanner === bannerUrl}
            onSelect={onSelect}
          />
        ))}
      </div>
      <div className={styles.galleryPreview}>
        {selectedBanner ? (
          <div
            className={styles.galleryPreviewImage}
            style={{ backgroundImage: `url('${selectedBanner}')` }}
          />
        ) : (
          <div className={styles.galleryPreviewPlaceholder}>
            {placeholder && (
              <p className={styles.galleryPreviewPlaceholderText}>
                {placeholder}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(BannerGallery);
